import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */

let babybarMealIndex;
const normalizeMealSearch = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
async function getBabybarMeals() {
  if (!babybarMealIndex) {
    const url = new URL(Theme.routes.root_url || '/', location.origin);
    url.pathname = url.pathname.replace(/\/$/, '') + '/pages/maaltijden';
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    babybarMealIndex = fetch(url).then(response => {
      if (!response.ok) throw new Error('Meal index unavailable');
      return response.text();
    }).then(markup => {
      const root = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-bb-meal-index]');
      if (!root) throw new Error('Meal index missing');
      return { pageLabel: root.dataset.bbPageLabel, label: root.dataset.bbLabel, url: root.dataset.bbUrl, meals: Array.from(root.querySelectorAll('[data-bb-meal-id]')).map(card => ({ id: card.dataset.bbMealId, title: card.dataset.bbTitle, age: card.dataset.bbAge, image: card.dataset.bbImage, text: card.dataset.bbSearch.split(/allergenen\s*:/i)[0].replace(/[^.!?]*(?:nog geen|nog niet|nog even weg|komt in de volgende fase)[^.!?]*[.!?]?/gi, '') })) };
    }).catch(error => { babybarMealIndex = null; throw error; });
  }
  return babybarMealIndex;
}

let babybarFAQIndex;
async function getBabybarFAQ() {
  if (!babybarFAQIndex) {
    const url = new URL(Theme.routes.root_url || '/', location.origin);
    url.pathname = url.pathname.replace(/\/$/, '') + '/pages/faq';
    babybarFAQIndex = fetch(url).then(response => {
      if (!response.ok) throw new Error('FAQ unavailable');
      return response.text();
    }).then(markup => {
      const main = new DOMParser().parseFromString(markup, 'text/html').querySelector('main');
      if (!main) throw new Error('FAQ content missing');
      return { label: main.querySelector('h1,h2,h3')?.textContent.trim() || 'FAQ', url: url.pathname, meals: Array.from(main.querySelectorAll('details')).filter(item => item.querySelector('summary')).map(item => ({ id: item.querySelector('summary').textContent.trim(), title: item.querySelector('summary').textContent.trim(), text: item.textContent, age: '', faq: true })) };
    }).catch(error => { babybarFAQIndex = null; throw error; });
  }
  return babybarFAQIndex;
}


let babybarPageIndex;
async function getBabybarPages() {
  if (!babybarPageIndex) {
    const links = new Map();
    for (const link of document.querySelectorAll('header a[href], footer a[href], [class*="header"] a[href], [class*="footer"] a[href]')) {
      const url = new URL(link.href, location.origin);
      if (url.origin !== location.origin || !/\/(pages|policies)\//.test(url.pathname) || /\/pages\/(maaltijden|faq)$/.test(url.pathname)) continue;
      links.set(url.pathname, link.textContent.trim());
    }
    babybarPageIndex = Promise.all(Array.from(links, async ([path, title]) => {
      try {
        const response = await fetch(path);
        if (!response.ok) return null;
        const main = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('main');
        if (!main) return null;
        for (const element of main.querySelectorAll('script,style')) element.remove();
        return { title: title || main.querySelector('h1,h2,h3')?.textContent.trim(), text: main.textContent, age: '', url: path };
      } catch { return null; }
    })).then(async pages => ({ kind: 'pages', label: (await getBabybarMeals()).pageLabel, url: '', meals: pages.filter(Boolean) }));
  }
  return babybarPageIndex;
}

function renderBabybarMeals(index, term, predictive = false) {
  const words = normalizeMealSearch(term).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return null;
  const matches = index.meals.filter(meal => {
    const text = normalizeMealSearch(meal.text);
    return words.every(word => text.includes(word));
  });
  if (!matches.length) return null;
  const section = document.createElement('section');
  section.dataset.bbMealResults = '';
  section.style.padding = predictive ? '16px 24px 24px' : '16px 0 24px';
  const heading = document.createElement('h2');
  heading.className = 'predictive-search-results__title';
  heading.textContent = index.label;
  heading.style.margin = '0 0 14px';
  heading.id = (predictive ? 'bb-predictive-' : 'bb-search-') + (index.kind || (index.url.includes('/faq') ? 'faq' : 'meals'));
  section.append(heading);
  const list = document.createElement('ul');
  list.className = 'predictive-search-results__list list-unstyled';
  list.setAttribute('aria-labelledby', heading.id);
  if (predictive) list.setAttribute('role', 'listbox');
  list.style.display = 'grid';
  list.style.gridTemplateColumns = 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))';
  list.style.gap = '12px';
  for (const meal of matches) {
    const item = document.createElement('li');
    if (predictive) {
      item.setAttribute('ref', 'resultsItems[]');
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
    }
    const link = document.createElement('a');
    const url = new URL(meal.url || index.url, location.origin);
    if (!meal.url) url.searchParams.set(meal.faq ? 'vraag' : 'gerecht', meal.id);
    link.href = url.pathname + url.search;
    if (meal.image) {
      const image = document.createElement('img');
      image.src = meal.image;
      image.alt = '';
      image.width = 72;
      image.height = 72;
      image.loading = 'lazy';
      image.style.width = '72px';
      image.style.height = '72px';
      image.style.objectFit = 'cover';
      image.style.borderRadius = '8px';
      image.style.flexShrink = '0';
      link.append(image);
    }
    const text = document.createElement('span');
    text.style.minWidth = '0';
    const title = document.createElement('span');
    title.textContent = meal.title;
    title.style.display = 'block';
    title.style.fontWeight = '600';
    title.style.lineHeight = '1.4';
    text.append(title);
    if (meal.age) {
      const age = document.createElement('span');
      age.textContent = meal.age;
      age.style.display = 'block';
      age.style.marginTop = '6px';
      age.style.fontSize = '0.875em';
      text.append(age);
    }
    link.append(text);
    item.style.display = 'flex';
    item.style.minWidth = '0';
    link.style.display = 'flex';
    link.style.gap = '12px';
    link.style.alignItems = 'center';
    link.style.width = '100%';
    link.style.height = '100%';
    link.style.boxSizing = 'border-box';
    link.style.textDecoration = 'none';
    link.style.padding = '12px';
    link.style.border = '1px solid var(--color-border)';
    link.style.borderRadius = 'var(--style-border-radius-inputs)';
    item.append(link);
    list.append(item);
  }
  section.append(list);
  return section;
}
onDocumentLoaded(async () => {
  const host = document.querySelector('[data-bb-full-search]');
  const term = new URLSearchParams(location.search).get('q');
  const question = new URLSearchParams(location.search).get('vraag');
  if (question && location.pathname.endsWith('/pages/faq')) {
    const answer = Array.from(document.querySelectorAll('main details')).find(item => item.querySelector('summary')?.textContent.trim() === question);
    if (answer) { answer.open = true; answer.scrollIntoView({ block: 'center', behavior: 'instant' }); }
  }
  if (!host || !term) return;
  try {
    const indexes = await Promise.all([getBabybarMeals().catch(() => null), getBabybarFAQ().catch(() => null), getBabybarPages().catch(() => null)]);
    for (const index of indexes) {
      if (!index) continue;
      const results = renderBabybarMeals(index, term);
      if (results) host.append(results);
    }
  } catch {}
});

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'product,collection,page,article,query');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then(async (resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        const parsed = new DOMParser().parseFromString(resultsMarkup, 'text/html');
        const resultRoot = parsed.querySelector('#predictive-search-results');
        try {
          const indexes = await Promise.all([getBabybarMeals().catch(() => null), getBabybarFAQ().catch(() => null), getBabybarPages().catch(() => null)]);
          for (const index of indexes.reverse()) {
            if (!index) continue;
            const additionalResults = renderBabybarMeals(index, searchTerm, true);
            if (additionalResults && resultRoot) {
              resultRoot.querySelector('.predictive-search-results__inner')?.prepend(additionalResults);
              resultRoot.querySelector('[data-single-result-url]')?.remove();
              resultRoot.querySelector('.predictive-search-results__no-results')?.remove();
            }
          }
        } catch {}
        if (abortController.signal.aborted || !this.isConnected) return;
        const added = resultRoot?.querySelectorAll('[data-bb-meal-results]');
        if (added?.length) {
          const status = resultRoot.querySelector('[role="status"]');
          if (status) status.textContent = Array.from(added, section => section.querySelector('h2').textContent + ': ' + section.querySelectorAll('li').length).join('. ');
        }
        morph(predictiveSearchResults, resultRoot || resultsMarkup);
        this.refs.searchInput.setAttribute('aria-expanded', 'true');

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    searchInput.setAttribute('aria-expanded', 'false');
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      for (const card of recentlyViewedProductsHtml.querySelectorAll('.predictive-search-results__card')) {
      const link = card.querySelector('a[href*="/products/"]');
      if (!link || !(link.textContent.trim() || link.getAttribute('aria-label')?.trim())) card.remove();
    }
    const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
